import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Legend, ComposedChart
} from 'recharts';
import { AURAVEX_LOGO } from './logo';
import { INR, PLANS, Badge, Dot, KPI, Panel, Prog, Pill, BtnStyles, Btn, Inp, Sel, Textarea, Modal, Toast, DataTable, PageHeader, AlertBox, Grid, Divider, CameraLiveModal } from './utils';

// ============================================================
// LIVE API INTEGRATION - AURAVEX Backend
// ============================================================
const API_NODE   = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';
const API_PYTHON = (typeof process !== 'undefined' && process.env?.REACT_APP_PYTHON_URL)
  ? process.env.REACT_APP_PYTHON_URL : 'http://localhost:8000';
const WS_URL_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL)
  ? process.env.REACT_APP_WS_URL : 'ws://localhost:5000/ws';

const apiFetch = async (path, opts = {}, base = API_NODE) => {
  try {
    const token = localStorage.getItem('auravex_token');
    const res = await fetch(base + path, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...opts,
    });
    if (res.status === 401) {
      localStorage.removeItem('auravex_token');
      localStorage.removeItem('auravex_user');
      window.dispatchEvent(new CustomEvent('auravex:401'));
      return null;
    }
    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try { const j = await res.json(); errMsg = j.error || j.message || errMsg; } catch {}
      throw new Error(errMsg);
    }
    return await res.json();
  } catch (e) {
    console.warn(`[API] ${opts.method||'GET'} ${path}:`, e.message);
    throw e;  // re-throw so callers can handle it
  }
};
const pyFetch   = (p, o={}) => apiFetch(p, o, API_PYTHON);
const apiPost   = (p, d) => apiFetch(p, { method: 'POST',   body: JSON.stringify(d) });
const apiPut    = (p, d) => apiFetch(p, { method: 'PUT',    body: JSON.stringify(d) });
const apiPatch  = (p, d) => apiFetch(p, { method: 'PATCH',  body: JSON.stringify(d) });
const apiDelete = (p)    => apiFetch(p, { method: 'DELETE' });

// Map DB snake_case -> frontend camelCase
const mapTenant = r => r ? ({
  ...r,
  plan:    r.plan_id   ?? r.plan   ?? 'starter',
  maxCam:  r.max_cameras ?? r.max_cam ?? r.maxCam ?? 0,
  maxU:    r.max_users   ?? r.max_u   ?? r.maxU   ?? 0,
}) : null;
const mapCamera = r => r ? ({
  ...r,
  siteId:  r.site_id    ?? r.siteId  ?? '',
  res:     r.resolution ?? r.res     ?? '1080p',
  models:  Array.isArray(r.models) ? r.models : (typeof r.models==='string' ? JSON.parse(r.models||'[]') : []),
}) : null;
const mapEvent  = r => r ? ({
  ...r,
  caseId:  r.case_id   ?? r.caseId  ?? '',
  tId:     r.tenant_id ?? r.t_id    ?? r.tId    ?? '',
  siteId:  r.site_id   ?? r.siteId  ?? '',
  camId:   r.cam_id    ?? r.camId   ?? '',
  sev:     r.severity  ?? r.sev     ?? 'Medium',
  evidence: Array.isArray(r.evidence) ? r.evidence : [],
}) : null;
const mapUser   = r => r ? ({
  id:        r.id,
  name:      r.name,
  email:     r.email,
  role:      r.role,
  tenantId:  r.tenant_id   ?? r.tenantId ?? '',
  status:    r.status      ?? 'Active',
  mfa:       r.mfa_enabled ?? r.mfa      ?? false,
  last:      r.last_login  ?? r.last_seen ?? r.last ?? '',
  mobiles:   Array.isArray(r.mobiles)  ? r.mobiles  : (typeof r.mobiles ==='string' ? JSON.parse(r.mobiles  ||'[]') : []),
  emails:    Array.isArray(r.emails)   ? r.emails   : (typeof r.emails  ==='string' ? JSON.parse(r.emails   ||'[]') : [r.email||'']),
  notifyVia: (typeof r.notify_via==='object' && r.notify_via!==null) ? r.notify_via : (typeof r.notify_via==='string' ? JSON.parse(r.notify_via||'{}') : r.notifyVia || {email:true}),
}) : null;
const mapJetson = r => r ? ({ ...r, siteId: r.site_id??r.siteId, tId: r.tenant_id??r.t_id??r.tId }) : null;
const mapViol   = r => r ? ({ ...r, camId: r.cam_id??r.camId }) : null;
/* -- API payload converters (Frontend → Backend field names) -- */
const tenantToAPI  = t => ({ name:t.name, sector:t.sector, plan_id:t.plan||t.plan_id||'starter', contact:t.contact||'', billing:t.billing||'monthly', renewal:t.renewal||null, status:t.status, onboarded:t.onboarded||false });
const cameraToAPI  = c => ({ site_id:c.siteId||c.site_id, name:c.name, brand:c.brand||'Hikvision', type:c.type||'Fixed', ip:c.ip||'', resolution:c.res||c.resolution||'1080p', fps:c.fps||25, models:c.models||[], retention:c.retention||'30d', zone:c.zone||'', status:c.status, health:c.health });
const userToAPI    = u => ({ name:u.name, email:u.email||(u.emails&&u.emails[0])||'', role:u.role, tenant_id:u.tenantId||u.tenant_id||null, mobiles:u.mobiles||[], emails_list:u.emails||[], notify_via:u.notifyVia||u.notify_via||{email:true}, mfa_enabled:u.mfa||false, status:u.status });
const ruleToAPI    = r => ({ name:r.name, trigger:r.trigger||'', condition:r.condition||'', action:r.action||'', tenant_id:r.tId||r.tenant_id||'All', status:r.status||'Active' });

/* -- SECTOR AVATARS ------------------------------------------- */
const AVATAR_GOV  = "data:image/jpeg;base64,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";   // Male  - Government
const AVATAR_PVT  = "data:image/jpeg;base64,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"; // Female - Private / Industrial
const sectorAvatar = (sector) =>
  (sector==="Government"||sector==="Municipal"||sector==="Defence"||sector==="Police")
    ? AVATAR_GOV : AVATAR_PVT;

/* -- DESIGN TOKENS -- CLEAN WHITE LIGHT THEME ------------------- */
const T = {
  /* -- Surface hierarchy -- */
  bg:"#F0F2F7",     /* Page background -- very light grey-blue */
  deep:"#FFFFFF",   /* Sidebar background */
  panel:"#FFFFFF",  /* Card panels */
  card:"#FFFFFF",   /* Inline cards */
  border:"#E2E8F3", /* Subtle borders */
  hi:"#EEF2FB",     /* Hover highlight */
  glow:"#DDE5F6",   /* Active glow */
  faint:"#F7F9FD",  /* Input backgrounds */
  /* Gold primary -- AURAVEX brand */
  accent:"#C48A0E", accentD:"#9A6B00", accentLo:"#C48A0E18",
  /* Electric blue */
  blue:"#1A5FB4",   blueLo:"#1A5FB414",
  /* Green */
  green:"#1E8A55",  greenLo:"#1E8A5514",
  /* Standard semantic */
  red:"#C0392B",    redLo:"#C0392B12",
  purple:"#6527BE", purpleLo:"#6527BE12",
  teal:"#0E7490",   tealLo:"#0E749012",
  orange:"#C05800", orangeLo:"#C0580012",
  pink:"#A21060",   pinkLo:"#A2106012",
  /* Text -- dark on white */
  text:"#1A2235",   muted:"#7A90B0",
  /* Silver */
  silver:"#4A607A",
  font:"'DM Sans',system-ui,sans-serif",
  mono:"'IBM Plex Mono','Fira Mono',monospace",
};

const sc  = s => ({Online:T.green,Active:T.green,Stable:T.green,Resolved:T.green,
  Offline:T.red,Suspended:T.red,Locked:T.red,Critical:T.red,
  Degraded:T.accent,Acknowledged:T.accent,High:T.accent,
  Medium:T.orange,Beta:T.orange,Inactive:T.muted,
  Low:T.muted,Info:T.blue,Trial:T.purple,Open:T.red,
  "Challan Issued":T.blue,"Challan Pending":T.orange,
  "Court Referral":T.purple,Disputed:T.pink}[s]||T.muted);

/* -- PLANS (INR) -------------------------------------------------- */

/* -- TENANTS ------------------------------------------------------ */
const TENANTS_INIT = []; // Live data from API — add tenants via UI

/* -- SITES -------------------------------------------------------- */
const SITES_DATA = []; // Live data from API — populated via /api/cameras/sites/all

/* -- CAMERAS ------------------------------------------------------ */
const CAMERAS_INIT = []; // Live data from API

/* -- EVENTS ------------------------------------------------------- */
const EVENTS_INIT = []; // Live data from API

/* -- VIOLATIONS (TRAFFIC) ----------------------------------------- */
const VIOLS_INIT = []; // Live data from API

/* -- MODELS ------------------------------------------------------- */
const MODELS_INIT = []; // Loaded from API (/api/models) — seeded from schema.sql

/* -- RULES -------------------------------------------------------- */
const RULES_INIT = []; // Live data from API

/* -- USERS -------------------------------------------------------- */
const USERS_INIT = []; // Live data from API

/* -- JETSON NODES ------------------------------------------------- */
const JETSONS_INIT = []; // Live data from API

/* -- AUDIT LOG ---------------------------------------------------- */
const AUDIT_INIT = []; // Live data from API

/* -- REPORT TEMPLATES --------------------------------------------- */
const REPORTS_INIT = []; // Live data from API

/* -- CHART DATA --------------------------------------------------- */
const alertHours = Array.from({length:12},(_,i)=>({
  h:`${String(i*2).padStart(2,"0")}:00`,
  critical:Math.floor(Math.random()*4)+1,
  high:Math.floor(Math.random()*7)+2,
  medium:Math.floor(Math.random()*10)+3,
}));
const trafficHours = Array.from({length:12},(_,i)=>({
  h:`${String(i*2).padStart(2,"0")}:00`,
  violations:Math.floor(Math.random()*18)+2,
  vehicles:Math.floor(Math.random()*900)+200,
}));
const co2Data = [
  {m:"Jan",co2:12.4,fuel:520,saved:28000,idle:310},
  {m:"Feb",co2:11.8,fuel:492,saved:31000,idle:285},
  {m:"Mar",co2:10.9,fuel:455,saved:35000,idle:260},
  {m:"Apr",co2:10.1,fuel:421,saved:38500,idle:238},
  {m:"May",co2:9.4, fuel:392,saved:42000,idle:212},
  {m:"Jun",co2:8.6, fuel:358,saved:47000,idle:190},
];
const perfWeeks = [
  {w:"W1",ppe:95.5,anpr:97.8,fire:96.2,crowd:91.0},
  {w:"W2",ppe:96.2,anpr:98.1,fire:96.9,crowd:92.1},
  {w:"W3",ppe:97.1,anpr:98.6,fire:97.8,crowd:93.4},
  {w:"W4",ppe:97.8,anpr:99.1,fire:98.5,crowd:94.1},
];
const radarData = [
  {s:"Compliance",v:94},{s:"Response",v:88},{s:"Coverage",v:92},
  {s:"Uptime",v:99},{s:"Accuracy",v:97},{s:"Cost Eff.",v:83},
];

/* -- AI AGENT RESPONSES ------------------------------------------- */
export const AI_RESPONSES = {}; // canned answers removed 2026-11-04: the copilot answers from live data via /api/copilot
export const CHAT_INIT = [{role:"ai",msg:"Hello! I'm the <b>AURAVEX AI Copilot</b>. I answer only from your organisation's live data — cameras, AI Edge Servers, events, models and licences. Ask a question or pick a quick prompt.",time:"now"}];
/* ================================================================
   ATOMIC COMPONENTS
================================================================ */

/* Badge */

export const PageInfra = ({jetsons,setJetsons,toast,logAudit}) => {
  const [selNode,setSelNode] = useState(null);
  const [editJ,setEditJ]    = useState(null);
  const [delJ,setDelJ]      = useState(null);
  const [showAddJ,setShowAddJ] = useState(false);
  const [addJForm,setAddJForm] = useState({name:"",ip:"",model:"AGX Orin 64GB",ver:"v1.3",cameras:25});
  const ajf = k => v => setAddJForm(p=>({...p,[k]:v}));
  const saveEdit=()=>{ setJetsons(p=>p.map(j=>j.id===editJ.id?editJ:j)); logAudit&&logAudit("JETSON_EDITED",editJ.id,"Info"); setEditJ(null); toast(`${editJ.id} updated`); };
  const addJetsonNode=()=>{
    if(!addJForm.name||!addJForm.ip){ toast("Name and IP required","warn"); return; }
    const newJ={
      id:`J${String(jetsons.length+1).padStart(3,"0")}`,
      name:addJForm.name, ip:addJForm.ip, model:addJForm.model,
      ver:addJForm.ver, cameras:parseInt(addJForm.cameras)||0,
      status:"Online", cpu:25, gpu:30, ram:40, temp:"42degC", uptime:"0d 0h",
    };
    setJetsons(p=>[...p,newJ]);
    logAudit&&logAudit("JETSON_ADDED",newJ.id,"Info");
    setShowAddJ(false); setAddJForm({name:"",ip:"",model:"AGX Orin 64GB",ver:"v1.3",cameras:25});
    toast(`✅ ${newJ.name} (${newJ.id}) added`);
  };
  const delJetson=()=>{ setJetsons(p=>p.filter(j=>j.id!==delJ.id)); logAudit&&logAudit("JETSON_DELETED",delJ.id,"High"); setDelJ(null); toast(`${delJ.id} removed`,"warn"); };
  const rebootNode = id => {
    setJetsons(p=>p.map(j=>j.id===id?{...j,status:"Online",cpu:Math.floor(Math.random()*30)+20,gpu:Math.floor(Math.random()*40)+30,ram:Math.floor(Math.random()*30)+30,uptime:"0d 0h"}:j));
    logAudit&&logAudit("JETSON_REBOOTED",id,"Info");
    toast(`${id} reboot initiated`);
  };
  const sel = jetsons.find(j=>j.id===selNode);
  const statusColor = s => ({Online:T.green,Degraded:T.accent,Offline:T.red}[s]||T.muted);
  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Infrastructure -- AI Edge Servers" sub="On-premises compute - TensorRT inference - Camera pipeline management - health monitoring">
        <Badge text="On-Premises" color={T.green} sm/>
        <Btn v="ghost" sm onClick={()=>toast("Firmware update check initiated")}>Firmware Update</Btn>
        <Btn sm onClick={()=>setShowAddJ(true)}>+ Add Node</Btn>
      </PageHeader>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Total Nodes"   value={jetsons.length}                                           icon="⚡" color={T.blue}/>
        <KPI label="Online"        value={jetsons.filter(j=>j.status==="Online").length}            icon="✅" color={T.green}/>
        <KPI label="Degraded"      value={jetsons.filter(j=>j.status==="Degraded").length}          icon="⚠️" color={T.accent}/>
        <KPI label="Total Cameras" value={jetsons.reduce((a,j)=>a+j.cameras,0)} sub="On all nodes" icon="📹" color={T.teal}/>
      </div>
      {/* Node Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
        {jetsons.map(j=>{
          const c = statusColor(j.status);
          return(
            <div key={j.id}
              onClick={()=>setSelNode(selNode===j.id?null:j.id)}
              style={{background:selNode===j.id?T.blueLo:T.card,border:`1px solid ${selNode===j.id?T.blue+"55":c+"30"}`,borderRadius:10,padding:16,cursor:"pointer",transition:"all .14s",position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:c}}/>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
                <div>
                  <div style={{fontWeight:800,fontSize:13,color:T.text}}>{j.name}</div>
                  <div style={{fontSize:10,color:T.muted,fontFamily:T.mono,marginTop:1}}>{j.id} - {j.model}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <Dot s={j.status}/>
                  <div style={{fontSize:9,color:T.muted,marginTop:3}}>{j.ver}</div>
                </div>
              </div>
              {/* Gauges */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
                {[["CPU",j.cpu,T.blue],["GPU",j.gpu,T.green],["RAM",j.ram,T.purple]].map(([l,v,clr])=>(
                  <div key={l} style={{background:T.deep,borderRadius:7,padding:"8px 10px",textAlign:"center"}}>
                    <div style={{fontSize:16,fontWeight:800,color:v>85?T.red:v>70?T.accent:clr,fontFamily:T.mono}}>{j.status==="Offline"?"--":v+"%"}</div>
                    <div style={{fontSize:8,color:T.muted,marginBottom:4}}>{l}</div>
                    <Prog val={j.status==="Offline"?0:v} color={clr} h={3}/>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:10,marginBottom:10}}>
                {[["Cameras",j.cameras],["Temp",j.temp],["Uptime",j.uptime],["IP",j.ip]].map(([l,v])=>(
                  <div key={l}><span style={{color:T.muted}}>{l}: </span><span style={{fontWeight:600,color:T.text,fontFamily:T.mono}}>{v}</span></div>
                ))}
              </div>
              <div style={{display:"flex",gap:6}} onClick={e=>e.stopPropagation()}>
                <Btn sm v="blue" full onClick={()=>rebootNode(j.id)}>🔄 Reboot</Btn>
                <Btn sm v="ghost" onClick={()=>setEditJ({...j})}>✏</Btn>
                <Btn sm v="danger" onClick={()=>setDelJ(j)}>🗑</Btn>
              </div>
              {selNode===j.id&&(
                <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.border}`}}>
                  <div style={{fontSize:9,color:T.muted,letterSpacing:1,marginBottom:8,fontWeight:700}}>DOCKER CONTAINERS</div>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {["auravex-inference","auravex-streamer","auravex-agent","nginx-proxy","redis-cache"].map((c,i)=>(
                      <div key={c} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.panel,borderRadius:5,padding:"5px 9px",fontSize:10}}>
                        <span style={{fontFamily:T.mono,color:T.text}}>{c}</span>
                        <Badge text={j.status==="Offline"?"STOPPED":i===4&&j.status==="Degraded"?"RESTART":"RUNNING"} xs color={j.status==="Offline"?T.red:i===4&&j.status==="Degraded"?T.accent:T.green}/>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:10,display:"flex",gap:6}}>
                    <Btn sm v="ghost" onClick={()=>toast("🖥️ SSH Terminal -- opening in new window")}>SSH Terminal</Btn>
                    <Btn sm v="ghost" onClick={()=>toast("v Pulling latest container images...")}>Pull Update</Btn>
                    <Btn sm v="warn" onClick={()=>toast("🔄 Restarting all containers...")}>Restart All</Btn>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Add AI Edge Server Modal */}
      <Modal open={showAddJ} title="+ Register AI Edge Server" onClose={()=>setShowAddJ(false)}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Grid cols={2} gap={12}>
            <Inp label="Node Name"    value={addJForm.name}     onChange={ajf("name")}     placeholder="Pune Gate Node 1"/>
            <Inp label="IP Address"   value={addJForm.ip}       onChange={ajf("ip")}       placeholder="192.168.10.50"/>
            <Sel label="AI Edge Server Model" value={addJForm.model}    onChange={ajf("model")}    options={["AGX Orin 64GB","AGX Orin 32GB","Orin NX 16GB","Orin NX 8GB","Nano 4GB"]}/>
            <Inp label="Software Ver"  value={addJForm.ver}      onChange={ajf("ver")}      placeholder="v1.3"/>
            <Inp label="Max Cameras"  value={String(addJForm.cameras)} onChange={ajf("cameras")} placeholder="25" type="number"/>
          </Grid>
          <AlertBox type="info">i️ Node will be registered with AURAVEX inference engine. Ensure the edge software are installed before provisioning.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setShowAddJ(false)}>Cancel</Btn>
            <Btn onClick={addJetsonNode} disabled={!addJForm.name||!addJForm.ip}>⚡ Register Node</Btn>
          </div>
        </div>
      </Modal>

      {/* Edit AI Edge Server Modal */}
      <Modal open={!!editJ} title={`✏ Edit Node -- ${editJ?.id}`} onClose={()=>setEditJ(null)}>
        {editJ&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Grid cols={2} gap={12}>
            <Inp label="Node Name"  value={editJ.name}    onChange={v=>setEditJ(p=>({...p,name:v}))}    placeholder="Node name"/>
            <Inp label="IP Address" value={editJ.ip}      onChange={v=>setEditJ(p=>({...p,ip:v}))}      placeholder="192.168.1.x"/>
            <Inp label="Software Ver"value={editJ.ver}     onChange={v=>setEditJ(p=>({...p,ver:v}))}     placeholder="v1.3"/>
            <Sel label="Model"      value={editJ.model}   onChange={v=>setEditJ(p=>({...p,model:v}))}   options={["AGX Orin 64GB","AGX Orin 32GB","Orin NX 16GB","Orin NX 8GB"]}/>
            <Sel label="Status"     value={editJ.status}  onChange={v=>setEditJ(p=>({...p,status:v}))}  options={["Online","Degraded","Offline"]}/>
            <Inp label="Max Cameras" value={String(editJ.cameras||"")} onChange={v=>setEditJ(p=>({...p,cameras:parseInt(v)||p.cameras}))} placeholder="25"/>
          </Grid>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setEditJ(null)}>Cancel</Btn>
            <Btn onClick={saveEdit}>💾 Save Changes</Btn>
          </div>
        </div>}
      </Modal>

      {/* Delete AI Edge Server Confirm */}
      <Modal open={!!delJ} title="⚠️ Remove AI Edge Server" onClose={()=>setDelJ(null)}>
        {delJ&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <AlertBox type="error">🗑 Remove node <strong>{delJ.id} -- {delJ.name}</strong>? All {delJ.cameras} cameras assigned to this node will lose edge inference.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setDelJ(null)}>Cancel</Btn>
            <Btn v="danger" onClick={delJetson}>🗑 Confirm Remove</Btn>
          </div>
        </div>}
      </Modal>

      {/* Sizing Guide */}
      <Panel title="EDGE SERVER SIZING GUIDE" accent={T.blue}>
        <DataTable dense cols={[
          {k:"model",l:"Model",         r:v=><span style={{fontWeight:700,fontFamily:T.mono}}>{v}</span>},
          {k:"cameras",l:"Cameras (Max)"},
          {k:"models",l:"Concurrent Models"},
          {k:"fps",l:"Total FPS"},
          {k:"tdp",l:"TDP"},
          {k:"ram",l:"Unified RAM"},
          {k:"price",l:"Approx Price (INR)"},
          {k:"use",l:"Use Case",r:v=><span style={{fontSize:10,color:T.muted}}>{v}</span>},
        ]} rows={[
          {model:"Orin NX 8GB",  cameras:8,  models:3, fps:"~120",tdp:"15W",ram:"8 GB",  price:INR(60000), use:"Small site, 8 cams, 3 models"},
          {model:"Orin NX 16GB", cameras:16, models:5, fps:"~240",tdp:"25W",ram:"16 GB", price:INR(95000), use:"Medium site, traffic / IT campus"},
          {model:"AGX Orin 32GB",cameras:25, models:8, fps:"~400",tdp:"40W",ram:"32 GB", price:INR(180000),use:"Large site, multi-model deployment"},
          {model:"AGX Orin 64GB",cameras:40, models:12,fps:"~600",tdp:"60W",ram:"64 GB", price:INR(280000),use:"Enterprise / government large site"},
        ]}/>
      </Panel>
    </div>
  );
};

/* -- AUDIT LOG ---------------------------------------------------- */
export const PageAudit = ({audit,toast}) => {
  const [sf,setSf] = useState("All");
  const sevColors  = {Critical:T.red,High:T.accent,Medium:T.orange,Info:T.blue};
  const filtered   = sf==="All"?audit:audit.filter(a=>a.sev===sf);
  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Audit Trail & Compliance Log" sub="Tamper-proof - All admin actions - SIEM export - ISO 27001 - Retention: 3 years">
        <Btn v="ghost" sm onClick={()=>toast("📤 Exporting to SIEM/Splunk")}>Export SIEM (Splunk)</Btn>
        <Btn v="ghost" sm onClick={()=>toast("📊 CSV export initiated")}>Export CSV</Btn>
        <Btn sm onClick={()=>toast("📥 Audit log download started")}>Download Full Log</Btn>
      </PageHeader>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Total Events"  value={audit.length}                              icon="📋" color={T.blue}/>
        <KPI label="Critical"      value={audit.filter(a=>a.sev==="Critical").length} icon="🔴" color={T.red}/>
        <KPI label="High"          value={audit.filter(a=>a.sev==="High").length}     icon="🟡" color={T.accent}/>
        <KPI label="Info / Medium" value={audit.filter(a=>["Info","Medium"].includes(a.sev)).length} icon="🔵" color={T.blue}/>
      </div>
      {/* Filters */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:1}}>SEVERITY:</span>
        {["All","Critical","High","Medium","Info"].map(s=>
          <Pill key={s} label={s} active={sf===s} onClick={()=>setSf(s)} color={sevColors[s]||T.blue}/>
        )}
      </div>
      <Panel title={`${filtered.length} AUDIT EVENTS`}>
        <DataTable cols={[
          {k:"id",    l:"Event ID",  r:v=><span style={{fontFamily:T.mono,color:T.blue,fontSize:10}}>{v}</span>},
          {k:"time",  l:"Time",      r:v=><span style={{fontFamily:T.mono,color:T.muted,fontSize:10}}>{v}</span>},
          {k:"actor", l:"Actor",     r:v=><span style={{fontFamily:T.mono,fontSize:10,color:v==="System"?T.teal:T.text}}>{v}</span>},
          {k:"action",l:"Action",    r:v=><span style={{fontWeight:700,fontFamily:T.mono,fontSize:11}}>{v}</span>},
          {k:"target",l:"Target",    r:v=><span style={{fontSize:10,color:T.muted}}>{v}</span>},
          {k:"ip",    l:"IP Address",r:v=><span style={{fontFamily:T.mono,fontSize:10,color:T.muted}}>{v}</span>},
          {k:"sev",   l:"Severity",  r:v=><Badge text={v} sm color={sevColors[v]||T.muted}/>},
        ]} rows={filtered}/>
      </Panel>
      {/* Compliance panel */}
      <Panel title="COMPLIANCE STANDARDS" accent={T.purple}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
          {[{std:"ISO 27001",st:"Compliant",last:"Jun 2025",next:"Jun 2026",c:T.green},{std:"ISO 14001",st:"Compliant",last:"Mar 2025",next:"Mar 2026",c:T.green},{std:"IT Act 2000",st:"Compliant",last:"Ongoing",next:"--",c:T.green},{std:"PDPB 2023",st:"In Progress",last:"--",next:"Sep 2025",c:T.accent}].map(({std,st,last,next,c})=>(
            <div key={std} style={{background:T.deep,borderRadius:8,padding:12,border:`1px solid ${c}30`}}>
              <div style={{fontSize:12,fontWeight:800,color:T.text,marginBottom:6}}>{std}</div>
              <Dot s={st==="Compliant"?"Active":"Acknowledged"}/>
              <div style={{fontSize:9,color:T.muted,marginTop:6}}>Last audit: {last}</div>
              <div style={{fontSize:9,color:T.muted}}>Next due: {next}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};

/* -- NOTIFICATIONS ------------------------------------------------ */
export const PageNotifications = ({toast,notifLog=[],sendNotification}) => {
  const [channels,setChannels] = useState({email:false,sms:false,whatsapp:false,webhook:false,slack:false});
  useEffect(()=>{ fetch("/api/alertrecipients/channels",{headers:{Authorization:"Bearer "+(localStorage.getItem("auravex_token")||"")}}).then(r=>r.ok?r.json():null).then(j=>{ if(j) setChannels(c=>({...c,...j})); }).catch(()=>{}); },[]);
  const toggle = k => setChannels(p=>({...p,[k]:!p[k]}));
  // -- Broadcast state
  const [broadSubject,setBroadSubject] = useState("");
  const [broadBody,setBroadBody]       = useState("");
  const [broadChs,setBroadChs]         = useState({email:true,sms:false,whatsapp:false});
  const [broadTo,setBroadTo]           = useState("all");
  const toggleBroadCh = ch => setBroadChs(p=>({...p,[ch]:!p[ch]}));
  const sendBroadcast = () => {
    if(!broadSubject||!broadBody) return toast("Fill subject and body","warn");
    const chs = Object.entries(broadChs).filter(([,v])=>v).map(([k])=>k);
    if(!chs.length) return toast("Select at least one channel","warn");
    const target = broadTo==="all"?"All users":"Admin users";
    sendNotification&&sendNotification(
      broadTo==="all"?"all@platform":"admin@auravex.ai",
      broadSubject, broadBody, chs,
      {name:target, emails:["all@platform"], mobiles:["+91 9000000000"],
       notifyVia:{email:broadChs.email,sms:broadChs.sms,whatsapp:broadChs.whatsapp}}
    );
    setBroadSubject(""); setBroadBody("");
    toast("✅ Broadcast sent to "+target);
  };

  const allNotifs = [...notifLog]; // demo notifications removed 2026-11-04: only real sends are listed

  const GATEWAYS = [
    {name:"Email",   icon:"📧",active:channels.email,   gateway:"SMTP", throughput:"", cost:""},
    {name:"SMS",     icon:"💬",active:channels.sms,     gateway:"Twilio SMS", throughput:"", cost:""},
    {name:"WhatsApp",icon:"📱",active:channels.whatsapp,gateway:"Twilio WhatsApp", throughput:"", cost:""},
    {name:"Webhook", icon:"🔗",active:channels.webhook, gateway:"Custom HTTP POST", throughput:"", cost:""},
    {name:"Slack",   icon:"💼",active:channels.slack,   gateway:"Slack Incoming Webhooks", throughput:"", cost:""},
  ];

  const delivered = allNotifs.filter(n=>n.status==="Delivered").length;
  const failed    = allNotifs.filter(n=>n.status!=="Delivered").length;
  const wa        = allNotifs.filter(n=>n.type==="WhatsApp").length;
  const emailCnt  = allNotifs.filter(n=>n.type==="Email").length;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Notification Center" sub="Multi-channel alerts - WhatsApp / SMS / Email / Webhook - Per-severity routing - Delivery tracking"/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Sent Today"  value={allNotifs.length} sub="All channels"             icon="📤" color={T.blue}/>
        <KPI label="Delivered"   value={delivered}        sub={`${Math.round(delivered/(allNotifs.length||1)*100)}% delivery rate`} icon="✅" color={T.green}/>
        <KPI label="Failed"      value={failed}           sub="Retry in queue"           icon="❌" color={T.red}/>
        <KPI label="WhatsApp"    value={wa}               sub={`Email: ${emailCnt}`}     icon="📱" color={T.teal}/>
      </div>

      {/* -- Channel config */}
      <Panel title="NOTIFICATION CHANNELS">
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}}>
          {GATEWAYS.map(({name,icon,active,gateway,throughput,cost},k)=>(
            <div key={name} style={{background:T.deep,borderRadius:9,padding:13,border:`1px solid ${active?T.green+"30":T.border}`,position:"relative",boxShadow:"0 1px 4px rgba(26,34,53,.06)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:20}}>{icon}</span>
                <button onClick={()=>toggle(["email","sms","whatsapp","webhook","slack"][k])}
                  style={{background:active?T.greenLo:T.hi,border:`1px solid ${active?T.green:T.border}`,borderRadius:20,padding:"3px 10px",cursor:"pointer",fontSize:10,fontWeight:700,color:active?T.green:T.muted,fontFamily:T.font}}>
                  {active?"ON":"OFF"}
                </button>
              </div>
              <div style={{fontSize:12,fontWeight:800,color:T.text,marginBottom:4}}>{name}</div>
              <div style={{fontSize:9,color:T.muted,lineHeight:1.6}}>{gateway}<br/>{active?"configured on server":"not configured on server"}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* -- Admin Broadcast Panel */}
      <Panel title="📢 ADMIN BROADCAST -- Send to All Users">
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",marginBottom:6}}>Send To</div>
              <div style={{display:"flex",gap:6}}>
                {[{v:"all",l:"All Users"},{v:"admins",l:"Admins Only"},{v:"gov",l:"Gov Users"},{v:"private",l:"Private Users"}].map(opt=>(
                  <button key={opt.v} onClick={()=>setBroadTo(opt.v)} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${broadTo===opt.v?T.blue+"55":T.border}`,background:broadTo===opt.v?T.blueLo:"transparent",cursor:"pointer",fontFamily:T.font,fontSize:10,color:broadTo===opt.v?T.blue:T.muted,fontWeight:broadTo===opt.v?700:400}}>{opt.l}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",marginBottom:6}}>Channels <span style={{color:T.muted,fontWeight:400}}>(optional)</span></div>
              <div style={{display:"flex",gap:6}}>
                {[{k:"email",icon:"📧",label:"Email"},{k:"sms",icon:"💬",label:"SMS"},{k:"whatsapp",icon:"📱",label:"WhatsApp"}].map(({k,icon,label})=>(
                  <button key={k} onClick={()=>toggleBroadCh(k)} style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",borderRadius:6,border:`1px solid ${broadChs[k]?T.green+"55":T.border}`,background:broadChs[k]?T.greenLo:"transparent",cursor:"pointer",fontFamily:T.font,fontSize:10,color:broadChs[k]?T.green:T.muted,fontWeight:broadChs[k]?700:400}}>
                    <span>{icon}</span><span>{label}</span>{broadChs[k]&&<span>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Inp label="Subject" value={broadSubject} onChange={setBroadSubject} placeholder="Platform maintenance scheduled for 2:00 AM IST"/>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:.8,textTransform:"uppercase"}}>Message Body</div>
            <textarea value={broadBody} onChange={e=>setBroadBody(e.target.value)} rows={3}
              placeholder={"Dear User,\n\nImportant platform update...\n\nTeam AURAVEX GLOBAL AI"}
              style={{background:T.faint,border:`1px solid ${T.border}`,borderRadius:6,padding:"8px 10px",fontSize:11,color:T.text,fontFamily:T.font,outline:"none",resize:"vertical",width:"100%"}}/>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Btn onClick={sendBroadcast} disabled={!broadSubject||!broadBody}>📢 Send Broadcast</Btn>
            <span style={{fontSize:10,color:T.muted}}>Notification will be logged in the delivery log below.</span>
          </div>
        </div>
      </Panel>

      {/* -- Routing rules */}
      <Panel title="SEVERITY -> CHANNEL ROUTING">
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
          {[{sev:"Critical",c:T.red,   ch:["WhatsApp","SMS","Email","Webhook"]},
            {sev:"High",    c:T.accent,ch:["WhatsApp","Email"]},
            {sev:"Medium",  c:T.orange,ch:["Email"]},
            {sev:"Low",     c:T.muted, ch:["Email (digest)"]}].map(({sev,c,ch})=>(
            <div key={sev} style={{background:T.deep,borderRadius:8,padding:11,border:`1px solid ${c}25`}}>
              <div style={{marginBottom:8}}><Badge text={sev} sm color={c}/></div>
              {ch.map(x=><div key={x} style={{fontSize:10,color:T.text,marginBottom:3,paddingLeft:7,borderLeft:`2px solid ${c}55`}}>-> {x}</div>)}
            </div>
          ))}
        </div>
      </Panel>

      {/* -- Notification log */}
      <Panel title={`DELIVERY LOG -- ${allNotifs.length} NOTIFICATIONS`} action={<Badge text="Live" color={T.green} xs/>}>
        <DataTable dense cols={[
          {k:"id",     l:"ID",       r:v=><span style={{fontFamily:T.mono,color:T.blue,fontSize:10}}>{v}</span>},
          {k:"time",   l:"Time",     r:v=><span style={{fontFamily:T.mono,color:T.muted,fontSize:10}}>{v}</span>},
          {k:"type",   l:"Channel",  r:v=><Badge text={v} sm color={{WhatsApp:T.teal,Email:T.blue,SMS:T.orange}[v]||T.muted}/>},
          {k:"to",     l:"Recipient",r:v=><span style={{fontFamily:T.mono,fontSize:10,color:T.muted}}>{v}</span>},
          {k:"subject",l:"Subject",  r:v=><span style={{fontSize:10,fontWeight:700}}>{v||"--"}</span>},
          {k:"msg",    l:"Message",  r:v=><span style={{fontSize:10,color:T.text,maxWidth:250,display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v||"--"}</span>},
          {k:"sev",    l:"Severity", r:v=><Badge text={v||"Info"} xs color={{Critical:T.red,High:T.accent,Medium:T.orange,Info:T.blue}[v]||T.muted}/>},
          {k:"status", l:"Status",   r:v=><Dot s={v==="Delivered"?"Online":"Offline"}/>},
        ]} rows={allNotifs}/>
      </Panel>
    </div>
  );
};
/* -- AI AGENT + AI AVATAR COPILOT --------------------------------- */
/* -- LANGUAGE CONFIG ------------------------------------------- */
export const SPEECH_LANGS = [
  {code:"en-IN",  label:"English (India)",    flag:"🇮🇳", short:"EN"},
  {code:"hi-IN",  label:"Hindi",      flag:"🇮🇳", short:"HI"},
  {code:"mr-IN",  label:"Marathi",     flag:"🇮🇳", short:"MR"},
  {code:"ta-IN",  label:"Tamil",       flag:"🇮🇳", short:"TA"},
  {code:"te-IN",  label:"Telugu",     flag:"🇮🇳", short:"TE"},
  {code:"bn-IN",  label:"Bengali",     flag:"🇮🇳", short:"BN"},
  {code:"gu-IN",  label:"Gujarati", flag:"🇮🇳", short:"GU"},
  {code:"kn-IN",  label:"Kannada",    flag:"🇮🇳", short:"KN"},
  {code:"pa-IN",  label:"Punjabi",   flag:"🇮🇳", short:"PA"},
  {code:"ml-IN",  label:"Malayalam", flag:"🇮🇳", short:"ML"},
  {code:"ur-IN",  label:"Urdu",         flag:"🇮🇳", short:"UR"},
  {code:"ar-SA",  label:"Arabic",    flag:"🇸🇦", short:"AR"},
  {code:"zh-CN",  label:"Chinese (Mandarin)",       flag:"🇨🇳", short:"ZH"},
  {code:"fr-FR",  label:"Français (French)",   flag:"🇫🇷", short:"FR"},
  {code:"de-DE",  label:"Deutsch (German)",    flag:"🇩🇪", short:"DE"},
  {code:"ja-JP",  label:"Japanese",   flag:"🇯🇵", short:"JA"},
  {code:"ko-KR",  label:"Korean",     flag:"🇰🇷", short:"KO"},
  {code:"pt-BR",  label:"Português (Brazil)",  flag:"🇧🇷", short:"PT"},
  {code:"ru-RU",  label:"Russian",   flag:"🇷🇺", short:"RU"},
  {code:"es-ES",  label:"Español (Spanish)",   flag:"🇪🇸", short:"ES"},
];

export const PageAIAgent = () => {
  const [chat,setChat]         = useState(CHAT_INIT);
  const [input,setInput]       = useState("");
  const [loading,setLoading]   = useState(false);
  const [listening,setListening]       = useState(false);
  const [langCode,setLangCode]         = useState("en-IN");
  const [showLangPicker,setShowLangPicker] = useState(false);
  const [ttsEnabled,setTtsEnabled]     = useState(true);
  const [speechSupport,setSpeechSupport] = useState(false);
  const [ttsSupport,setTtsSupport]     = useState(false);
  const [micPulse,setMicPulse]         = useState(false);
  const [transcript,setTranscript]     = useState("");
  const [speechError,setSpeechError]   = useState("");
  const [speakLoading,setSpeakLoading] = useState(false);
  const chatEnd   = useRef(null);
  const recogRef  = useRef(null);
  const synthRef  = useRef(null);

  useEffect(()=>{
    const hasSpeech = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    const hasSynth  = !!(window.speechSynthesis);
    setSpeechSupport(hasSpeech);
    setTtsSupport(hasSynth);
    // Check existing mic permission state (non-blocking)
    if(navigator?.permissions){
      navigator.permissions.query({name:"microphone"}).then(res=>{
        if(res.state==="denied"){
          setSpeechError("🔒 Microphone is blocked for this site. Click the 🔒 in your browser address bar -> Allow Microphone.");
        }
        res.onchange = ()=>{
          if(res.state==="granted") setSpeechError("");
          if(res.state==="denied") setSpeechError("🔒 Microphone blocked. Enable it in browser settings.");
        };
      }).catch(()=>{});
    }
  },[]);

  useEffect(()=>{
    chatEnd.current?.scrollIntoView({behavior:"smooth"});
  },[chat]);

  /* -- START listening -- getUserMedia first to trigger browser dialog -- */
  const startListening = async () => {
    setSpeechError(""); setTranscript("");
    // Request mic permission via getUserMedia -- this shows the browser's native allow dialog
    try {
      const stream = await (navigator.mediaDevices?.getUserMedia({audio:true, video:false}) || Promise.reject(new Error("MediaDevices API unavailable")));
      stream.getTracks().forEach(t=>t.stop()); // release the stream immediately
    } catch(err) {
      if(err.name==="NotAllowedError"||err.name==="PermissionDeniedError") {
        setSpeechError("🔒 Microphone blocked. Click the 🔒 padlock in your browser address bar -> Site settings -> Allow Microphone.");
      } else if(err.name==="NotFoundError"||err.name==="DevicesNotFoundError") {
        setSpeechError("🎤 No microphone detected. Please connect one and try again.");
      } else if(err.name==="NotReadableError") {
        setSpeechError("🎤 Microphone is in use by another app. Close it and retry.");
      } else {
        setSpeechError("Microphone unavailable: "+err.message);
      }
      return;
    }
    // Now start SpeechRecognition
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if(!SpeechRec){
      setSpeechError("⚠️ Speech recognition is not supported. Please use Google Chrome or Microsoft Edge.");
      return;
    }
    const recog = new SpeechRec();
    recog.lang = langCode;
    recog.interimResults = true;
    recog.maxAlternatives = 1;
    recog.continuous = false;
    recog.onstart  = ()=>{ setListening(true); setMicPulse(true); setTranscript(""); };
    recog.onresult = (e)=>{ const t=Array.from(e.results).map(r=>r[0].transcript).join(" "); setTranscript(t); setInput(t); };
    recog.onend    = ()=>{ setListening(false); setMicPulse(false); };
    recog.onerror  = (e)=>{
      setListening(false); setMicPulse(false);
      const errMap = {
        "not-allowed":           "🔒 Mic blocked. Allow microphone in your browser's address bar settings.",
        "no-speech":             "🔇 No speech detected. Speak clearly and try again.",
        "network":               "🌐 Network error with the speech service. Check your connection.",
        "aborted":               "Recording was stopped.",
        "audio-capture":         "🎤 Microphone capture failed. Check your mic connection.",
        "service-not-allowed":   "🔒 Speech service blocked. Open site in Chrome over HTTPS.",
        "bad-grammar":           "Grammar error in speech service.",
        "language-not-supported":"🌐 Selected language not supported on this device.",
      };
      setSpeechError(errMap[e.error]||`Speech error: ${e.error}`);
    };
    recogRef.current = recog;
    try { recog.start(); } catch(e){ setSpeechError("Could not start: "+e.message); }
  };

  /* -- STOP listening -- */
  const stopListening = () => {
    recogRef.current?.stop();
    setListening(false); setMicPulse(false);
  };

  /* -- SPEAK AI response (TTS) -- */
  /* -- Speak text in the selected language (translate if non-English) -- */
  const doSpeak = (text, lang) => {
    if(!ttsSupport) return;
    const clean = text.replace(/<[^>]+>/g,"").replace(/&[a-z]+;/gi,"").trim();
    if(!clean) return;
    const utt = new SpeechSynthesisUtterance(clean);
    utt.lang  = lang || langCode;
    utt.rate  = 0.95;
    utt.pitch = 1.05;
    // Wait for voices to load (needed on first call)
    const trySpeak = () => {
      const voices = window.speechSynthesis.getVoices();
      const match  = voices.find(v=>v.lang===utt.lang) ||
                     voices.find(v=>v.lang.startsWith(utt.lang.split("-")[0]));
      if(match) utt.voice = match;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utt);
      synthRef.current = utt;
    };
    if(window.speechSynthesis.getVoices().length > 0) trySpeak();
    else { window.speechSynthesis.onvoiceschanged = trySpeak; }
  };

  const speakText = async (text) => {
    if(!ttsEnabled || !ttsSupport) return;
    const clean = text.replace(/<[^>]+>/g,"").replace(/&[a-z]+;/gi,"").trim();
    if(!clean) return;
    // If English selected, speak directly
    if(langCode === "en-IN" || langCode === "en-US" || langCode === "en-GB") {
      doSpeak(clean, langCode); return;
    }
    doSpeak(clean, langCode); // no third-party translation calls from the browser
  };

  const stopSpeaking = () => { window.speechSynthesis?.cancel(); };

  const QUICK_PROMPTS = [
    {label:"📅 Last week summary",       key:"last week"},
    {label:"🔮 Predict next week risks",  key:"predict"},
    {label:"🚦 Top violation hotspots",   key:"violation"},
    {label:"⛑️ PPE compliance trend",     key:"ppe"},
    {label:"🌧️ Monsoon preparedness plan",key:"monsoon"},
    {label:"💰 Platform ROI & savings",   key:"cost"},
  ];

  const sendMsg = async (text) => {
    const q = (text || input).trim();
    if(!q) return;
    setTranscript("");
    setSpeechError("");
    const lang = SPEECH_LANGS.find(l=>l.code===langCode);
    const userMsg = {role:"user",msg:q,time:new Date().toLocaleTimeString(),lang:lang?.short||"EN"};
    setChat(p=>[...p,userMsg]);
    setInput(""); setLoading(true);
    try {
      // Build history for context (last 10 messages)
      const historyForApi = chat.slice(-10).map(m=>({
        role: m.role==='ai'?'assistant':'user',
        content: m.msg.replace(/<[^>]*>/g,'') // strip HTML for API
      }));
      const result = await pyFetch('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({ message: q, history: historyForApi, context: {} })
      });
      const reply = result?.reply || 'Unable to reach AI service. Please check backend connection.';
      setChat(p=>[...p,{role:"ai",msg:reply,time:new Date().toLocaleTimeString()}]);
      speakText(reply);
    } catch(err) {
      // Fallback to rule-based if API down
      const match = Object.entries(AI_RESPONSES).find(([k])=>q.toLowerCase().includes(k));
      const reply = match ? match[1] : `Query received: <b>"${q}"</b>. AI service temporarily unavailable — using local intelligence. Check Python FastAPI service at port 8000.`;
      setChat(p=>[...p,{role:"ai",msg:reply,time:new Date().toLocaleTimeString()}]);
      speakText(reply);
    } finally {
      setLoading(false);
    }
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14,height:"calc(100vh - 120px)"}}>
      <PageHeader title="AI Agent + AI Avatar Copilot" sub="Multilingual voice + text - 20 languages - Speech-to-text - Text-to-speech - Trend analysis - Predictive AI">
        <Badge text="GPT-4o Engine"     color={T.blue}   sm/>
        <Badge text="30-Day Context"    color={T.green}  sm/>
        <Badge text={`🎙 ${SPEECH_LANGS.find(l=>l.code===langCode)?.label||langCode}`} color={listening?T.red:T.teal} sm/>
        <Badge text="BETA"              color={T.purple} sm/>
      </PageHeader>

      <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:14,flex:1,minHeight:0}}>
        {/* Chat window */}
        <div style={{display:"flex",flexDirection:"column",background:T.card,border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden"}}>
          {/* Avatar header */}
          <div style={{padding:"10px 16px",background:`linear-gradient(135deg,${T.hi},${T.glow})`,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
            <img src={AVATAR_PVT} alt="AURAVEX AI Copilot"
              style={{width:48,height:48,borderRadius:"50%",objectFit:"cover",objectPosition:"top",border:`2px solid ${T.teal}66`,flexShrink:0,boxShadow:"0 3px 12px rgba(26,34,53,.2)"}}/>
            <div>
              <div style={{fontWeight:800,fontSize:13,color:T.text}}>AURAVEX GLOBAL AI Copilot</div>
              <div style={{fontSize:10,color:T.green,display:"flex",alignItems:"center",gap:4}}>
                <span style={{width:6,height:6,borderRadius:"50%",background:T.green}}/>
                {listening
                  ? <span style={{display:"flex",alignItems:"center",gap:4}}>
                      {[1,2,3,4,5].map(n=>(
                        <span key={n} style={{
                          display:"inline-block",width:3,background:T.red,borderRadius:2,
                          animation:`waveBar ${0.6+n*0.1}s ease-in-out infinite`,
                          animationDelay:`${n*0.1}s`
                        }}/>
                      ))}
                      <span style={{marginLeft:4,color:T.red}}>Recording...</span>
                    </span>
                  : "Online - Analysing platform data in real-time"}
              </div>
            </div>
            <div style={{marginLeft:"auto",display:"flex",gap:6}}>
              <Badge text="AI Avatar" color={T.purple} xs/>
              <Badge text="Predictive Mode" color={T.blue} xs/>
            </div>
          </div>

          {/* Messages */}
          <div style={{flex:1,overflowY:"auto",padding:14,display:"flex",flexDirection:"column",gap:10}}>
            {chat.map((m,i)=>(
              <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
                {/* AI avatar */}
                {m.role==="ai"&&(
                  <div style={{width:34,height:34,borderRadius:"50%",overflow:"hidden",flexShrink:0,marginTop:2,border:`2px solid ${T.teal}55`,boxShadow:`0 2px 8px ${T.teal}30`}}>
                    <img src={AVATAR_PVT} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}} alt="AI"/>
                  </div>
                )}
                <div style={{display:"flex",flexDirection:"column",gap:3,maxWidth:"72%"}}>
                  <div style={{
                    background:m.role==="user"?`linear-gradient(135deg,${T.accent},${T.accentD})`:T.faint,
                    border:`1px solid ${m.role==="user"?T.blue+"44":T.border}`,
                    borderRadius:m.role==="user"?"12px 4px 12px 12px":"4px 12px 12px 12px",
                    padding:"9px 12px",
                  }}>
                    <div style={{fontSize:11,color:T.text,lineHeight:1.7}} dangerouslySetInnerHTML={{__html:m.msg}}/>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:5,justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
                    <span style={{fontSize:8,color:T.muted,fontFamily:T.mono}}>{m.time}</span>
                    {/* Language badge if voice message */}
                    {m.lang&&m.lang!=="EN"&&(
                      <span style={{fontSize:8,padding:"1px 5px",borderRadius:4,background:T.blueLo,color:T.blue,fontWeight:700}}>{m.lang}</span>
                    )}
                    {/* Speak button on AI messages */}
                    {m.role==="ai"&&ttsSupport&&(
                      <button onClick={()=>speakText(m.msg)} title="Speak this reply in selected language"
                        disabled={speakLoading}
                        style={{background:speakLoading?T.greenLo:"none",
                          border:`1px solid ${speakLoading?T.green+"55":T.border}`,borderRadius:10,
                          padding:"1px 7px",cursor:speakLoading?"wait":"pointer",
                          fontSize:9,color:speakLoading?T.green:T.muted,fontFamily:T.font,
                          lineHeight:1.4,transition:"all .12s"}}
                        onMouseEnter={e=>{if(!speakLoading){e.currentTarget.style.color=T.teal;e.currentTarget.style.borderColor=T.teal+"55";}}}
                        onMouseLeave={e=>{if(!speakLoading){e.currentTarget.style.color=T.muted;e.currentTarget.style.borderColor=T.border;}}}>
                        {speakLoading?"⏳ Translating...":"🔊 Speak"}
                      </button>
                    )}
                  </div>
                </div>
                {/* User avatar */}
                {m.role==="user"&&(
                  <div style={{width:34,height:34,borderRadius:"50%",background:T.blueLo,border:`2px solid ${T.blue}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,marginTop:2,fontWeight:700,color:T.blue}}>U</div>
                )}
              </div>
            ))}
            {loading&&(
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{width:30,height:30,borderRadius:"50%",background:`linear-gradient(135deg,#1E3A7A,${T.accent})`,display:"flex",alignItems:"center",overflow:"hidden"}}><img src={AVATAR_PVT} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}} alt="AI"/></div>
                <div style={{background:T.panel,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 14px",display:"flex",gap:4,alignItems:"center"}}>
                  {[0,1,2].map(i=>(
                    <span key={i} style={{width:7,height:7,borderRadius:"50%",background:T.muted,display:"inline-block",animation:`aiPulse 1s ease-in-out ${i*0.22}s infinite`}}/>
                  ))}
                </div>
              </div>
            )}
            <div ref={chatEnd}/>
            {/* Floating recording overlay */}
            {listening&&(
              <div style={{position:"sticky",bottom:0,left:0,right:0,display:"flex",justifyContent:"center",padding:"6px 0"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 18px",borderRadius:30,
                  background:`${T.red}15`,border:`1px solid ${T.red}40`,backdropFilter:"blur(8px)"}}>
                  <div style={{display:"flex",alignItems:"flex-end",gap:2,height:20}}>
                    {[1,2,3,4,5,4,3,2,1].map((h,n)=>(
                      <div key={n} style={{
                        width:3,background:T.red,borderRadius:2,
                        height:4+h*2,
                        animation:`waveBar ${0.5+n*0.07}s ease-in-out infinite`,
                        animationDelay:`${n*0.08}s`,
                      }}/>
                    ))}
                  </div>
                  <span style={{fontSize:11,color:T.red,fontWeight:700}}>
                    🎙 Listening in {SPEECH_LANGS.find(l=>l.code===langCode)?.label||langCode}...
                  </span>
                  <button onClick={stopListening}
                    style={{padding:"3px 10px",borderRadius:12,border:`1px solid ${T.red}55`,
                      background:`${T.red}20`,cursor:"pointer",fontSize:10,color:T.red,fontFamily:T.font,fontWeight:700}}>
                    Stop ⏹
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* -- Voice + Text Input Bar -- */}
          <div style={{padding:"10px 12px",borderTop:`1px solid ${T.border}`,background:T.panel,flexShrink:0}}>
            {/* Speech error / live transcript */}
            {transcript&&!speechError&&(
              <div style={{marginBottom:8,padding:"6px 10px",borderRadius:7,fontSize:11,
                background:`${T.green}10`,border:`1px solid ${T.green}30`,
                color:T.green,display:"flex",alignItems:"center",gap:6}}>
                <span>🎙</span>
                <span style={{flex:1,fontStyle:"italic"}}>{transcript}</span>
                <span style={{fontSize:9,color:T.muted,flexShrink:0}}>Live</span>
              </div>
            )}
            {speechError&&(
              <div style={{marginBottom:8,borderRadius:9,overflow:"hidden",border:`1px solid ${T.red}35`}}>
                <div style={{padding:"8px 12px",background:`${T.red}12`,display:"flex",alignItems:"flex-start",gap:8}}>
                  <span style={{fontSize:18,flexShrink:0,marginTop:1}}>🔒</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.red,marginBottom:3}}>Microphone Access Required</div>
                    <div style={{fontSize:10,color:T.text,lineHeight:1.6}}>{speechError}</div>
                  </div>
                  <button onClick={()=>setSpeechError("")} style={{background:"none",border:"none",cursor:"pointer",color:T.muted,fontSize:14,padding:0,flexShrink:0}}>✕</button>
                </div>
                <div style={{padding:"7px 12px",background:`${T.red}07`,borderTop:`1px solid ${T.red}20`}}>
                  <div style={{fontSize:10,color:T.muted,fontWeight:700,marginBottom:4}}>HOW TO ALLOW:</div>
                  <div style={{fontSize:10,color:T.text,lineHeight:1.7}}>
                    <b>Chrome:</b> Click 🔒 in address bar -> Site settings -> Microphone -> Allow<br/>
                    <b>Edge:</b> Click 🔒 in address bar -> Permissions -> Microphone -> Allow<br/>
                    <b>Firefox:</b> Click 🛡️ in address bar -> Permissions -> Use the Microphone -> Allow<br/>
                    <b>Safari:</b> Safari menu -> Settings for This Website -> Microphone -> Allow
                  </div>
                  <button onClick={()=>{ setSpeechError(""); startListening(); }}
                    style={{marginTop:8,padding:"5px 14px",borderRadius:8,border:`1px solid ${T.blue}55`,
                      background:T.blueLo,cursor:"pointer",fontSize:10,fontWeight:700,color:T.blue,fontFamily:T.font}}>
                    🔄 Retry After Allowing
                  </button>
                </div>
              </div>
            )}

            {/* Language selector row */}
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,flexWrap:"wrap"}}>
              <span style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:.6,textTransform:"uppercase",flexShrink:0}}>Language:</span>
              {SPEECH_LANGS.slice(0,12).map(l=>(
                <button key={l.code} onClick={()=>{setLangCode(l.code);setShowLangPicker(false);}}
                  title={l.label}
                  style={{padding:"3px 8px",borderRadius:20,border:`1px solid ${langCode===l.code?T.blue+"60":T.border}`,
                    background:langCode===l.code?T.blueLo:"transparent",cursor:"pointer",
                    fontFamily:T.font,fontSize:10,color:langCode===l.code?T.blue:T.muted,
                    fontWeight:langCode===l.code?700:400,transition:"all .12s",lineHeight:1.4}}>
                  {l.short}
                </button>
              ))}
              <button onClick={()=>setShowLangPicker(p=>!p)}
                style={{padding:"3px 8px",borderRadius:20,border:`1px solid ${showLangPicker?T.accent+"60":T.border}`,
                  background:showLangPicker?`${T.accent}15`:"transparent",cursor:"pointer",
                  fontFamily:T.font,fontSize:10,color:showLangPicker?T.accent:T.muted,transition:"all .12s"}}>
                {showLangPicker?"^ less":"v more"}
              </button>
              {/* TTS toggle + lang indicator */}
              <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
                {ttsEnabled&&langCode!=="en-IN"&&langCode!=="en-US"&&langCode!=="en-GB"&&(
                  <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,
                    background:`${T.teal}18`,border:`1px solid ${T.teal}44`,color:T.teal,fontWeight:700}}>
                    {speakLoading?"⏳ Translating...":`🌐 ${SPEECH_LANGS.find(l=>l.code===langCode)?.label}`}
                  </span>
                )}
                <span style={{fontSize:9,color:T.muted}}>🔊</span>
                <button onClick={()=>{ setTtsEnabled(p=>!p); if(ttsEnabled) stopSpeaking(); }}
                  style={{padding:"3px 10px",borderRadius:20,border:`1px solid ${ttsEnabled?T.green+"55":T.border}`,
                    background:ttsEnabled?T.greenLo:"transparent",cursor:"pointer",
                    fontFamily:T.font,fontSize:10,fontWeight:700,
                    color:ttsEnabled?T.green:T.muted,transition:"all .12s"}}>
                  {ttsEnabled?"ON":"OFF"}
                </button>
                {ttsSupport&&ttsEnabled&&(
                  <button onClick={stopSpeaking} title="Stop speaking"
                    style={{padding:"3px 7px",borderRadius:20,border:`1px solid ${T.border}`,
                      background:"transparent",cursor:"pointer",fontSize:10,color:T.muted,fontFamily:T.font}}>
                    ⏹
                  </button>
                )}
              </div>
            </div>

            {/* Extended language picker */}
            {showLangPicker&&(
              <div style={{marginBottom:8,padding:"8px 10px",background:T.deep,borderRadius:8,border:`1px solid ${T.border}`,display:"flex",flexWrap:"wrap",gap:6}}>
                {SPEECH_LANGS.map(l=>(
                  <button key={l.code} onClick={()=>{setLangCode(l.code);setShowLangPicker(false);}}
                    title={l.label}
                    style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${langCode===l.code?T.blue+"60":T.border}`,
                      background:langCode===l.code?T.blueLo:"transparent",cursor:"pointer",
                      fontFamily:T.font,fontSize:10,color:langCode===l.code?T.blue:T.muted,
                      fontWeight:langCode===l.code?700:400,transition:"all .12s"}}>
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
            )}

            {/* Main input row */}
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {/* Mic button */}
              {speechSupport&&(
                <button
                  onClick={listening?stopListening:startListening}
                  title={listening?"Stop recording":"Start voice input"}
                  style={{
                    width:42,height:42,borderRadius:"50%",flexShrink:0,cursor:"pointer",
                    border:`2px solid ${listening?T.red+"80":T.blue+"60"}`,
                    background:listening?`${T.red}18`:`${T.blue}12`,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    transition:"all .2s",
                    boxShadow:listening?`0 0 0 ${micPulse?8:4}px ${T.red}22`:"none",
                    outline:"none",
                    animation:listening?"micPulse 1.2s ease-in-out infinite":"none",
                  }}>
                  <span style={{fontSize:18}}>{listening?"⏹":"🎙"}</span>
                </button>
              )}
              {!speechSupport&&(
                <div title="Speech not supported" style={{width:42,height:42,borderRadius:"50%",flexShrink:0,
                  border:`2px solid ${T.border}`,background:T.deep,display:"flex",alignItems:"center",
                  justifyContent:"center",fontSize:18,opacity:.35}}>🎙</div>
              )}

              {/* Text input */}
              <div style={{flex:1,position:"relative"}}>
                <input
                  value={input}
                  onChange={e=>{setInput(e.target.value);setTranscript("");}}
                  onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMsg()}
                  placeholder={listening
                    ? `🎙 Listening in ${SPEECH_LANGS.find(l=>l.code===langCode)?.label||langCode}...`
                    : `Type or tap 🎙 to speak in ${SPEECH_LANGS.find(l=>l.code===langCode)?.label||langCode}...`}
                  style={{width:"100%",background:listening?`${T.blue}08`:T.deep,
                    border:`1.5px solid ${listening?T.blue+"60":T.hi}`,borderRadius:22,
                    padding:"10px 40px 10px 14px",color:T.text,fontSize:12,fontFamily:T.font,
                    outline:"none",transition:"all .2s",boxSizing:"border-box"}}
                />
                {input&&(
                  <button onClick={()=>{setInput("");setTranscript("");}}
                    style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",
                      background:"none",border:"none",cursor:"pointer",color:T.muted,fontSize:14,
                      lineHeight:1,padding:0}}>✕</button>
                )}
              </div>

              {/* Send button */}
              <button onClick={()=>sendMsg()} disabled={!input.trim()||loading}
                style={{height:42,padding:"0 18px",borderRadius:22,flexShrink:0,
                  background:(!input.trim()||loading)?T.hi:`linear-gradient(135deg,${T.blue},${T.accent})`,
                  border:"none",cursor:(!input.trim()||loading)?"not-allowed":"pointer",
                  color:(!input.trim()||loading)?T.muted:T.text,fontFamily:T.font,
                  fontSize:12,fontWeight:700,transition:"all .18s",
                  boxShadow:(!input.trim()||loading)?"none":`0 4px 14px ${T.blue}40`}}>
                {loading?"...":"Send"}
              </button>
            </div>

            {/* Status bar */}
            <div style={{marginTop:5,display:"flex",alignItems:"center",gap:6,fontSize:9,color:T.muted}}>
              {!speechSupport
                ? <><span style={{color:T.accent}}>⚠</span><span style={{color:T.accent}}>Voice unavailable -- use Chrome or Edge</span></>
                : listening
                  ? <><span style={{color:T.red,fontWeight:700,animation:"blink 1s infinite"}}>●</span>
                      <span style={{color:T.red}}>Recording in {SPEECH_LANGS.find(l=>l.code===langCode)?.label||langCode}...</span></>
                  : <><span style={{color:T.green,fontWeight:700}}>●</span>
                      <span>Ready - Click 🎙 or tap mic to speak</span></>
              }
              <span style={{marginLeft:"auto",color:ttsEnabled&&ttsSupport?T.green:T.muted}}>
                {ttsEnabled&&ttsSupport?"🔊 Voice reply on":"🔇 Voice reply off"}
              </span>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div style={{display:"flex",flexDirection:"column",gap:12,overflowY:"auto"}}>
          {/* -- AI Avatar Card -- */}
          <Panel title="🤖 AI AGENT AVATAR">
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {/* Government avatar */}
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,padding:"10px 6px",background:T.deep,borderRadius:9,border:`1px solid ${T.blue}44`}}>
                  <img src={AVATAR_GOV} alt="Government Agent"
                    style={{width:64,height:64,borderRadius:10,objectFit:"cover",objectPosition:"top center",border:`2px solid ${T.blue}66`,boxShadow:"0 3px 12px rgba(26,34,53,.18)"}}/>
                  <div style={{fontSize:9,fontWeight:700,color:T.blue,textAlign:"center",letterSpacing:.6,textTransform:"uppercase"}}>🏛 Government</div>
                  <div style={{fontSize:8,color:T.muted,textAlign:"center"}}>Security & Civic AI</div>
                </div>
                {/* Private avatar */}
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,padding:"10px 6px",background:T.deep,borderRadius:9,border:`1px solid ${T.teal}44`}}>
                  <img src={AVATAR_PVT} alt="Private Agent"
                    style={{width:64,height:64,borderRadius:10,objectFit:"cover",objectPosition:"top center",border:`2px solid ${T.teal}66`,boxShadow:"0 3px 12px rgba(26,34,53,.18)"}}/>
                  <div style={{fontSize:9,fontWeight:700,color:T.teal,textAlign:"center",letterSpacing:.6,textTransform:"uppercase"}}>🏢 Private</div>
                  <div style={{fontSize:8,color:T.muted,textAlign:"center"}}>Industrial & Safety AI</div>
                </div>
              </div>
              <div style={{fontSize:9,color:T.muted,textAlign:"center",lineHeight:1.5,padding:"6px 8px",background:T.hi,borderRadius:6}}>
                Avatar auto-assigned based on<br/>tenant sector classification
              </div>
              {/* Current language indicator */}
              <div style={{padding:"8px 10px",background:T.deep,borderRadius:8,border:`1px solid ${listening?T.red+"40":T.green+"30"}`}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                  <span style={{fontSize:14}}>{SPEECH_LANGS.find(l=>l.code===langCode)?.flag||"🌐"}</span>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:T.text}}>{SPEECH_LANGS.find(l=>l.code===langCode)?.label||langCode}</div>
                    <div style={{fontSize:8,color:T.muted}}>Active speech language</div>
                  </div>
                  <div style={{marginLeft:"auto",width:8,height:8,borderRadius:"50%",background:listening?T.red:T.green,
                    boxShadow:listening?`0 0 8px ${T.red}`:`0 0 5px ${T.green}`,
                    animation:listening?"blink 1s infinite":"none"}}/>
                </div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  <span style={{fontSize:8,padding:"2px 6px",borderRadius:4,background:T.blueLo,color:T.blue}}>🎙 STT: {speechSupport?"Ready":"N/A"}</span>
                  <span style={{fontSize:8,padding:"2px 6px",borderRadius:4,background:ttsEnabled?T.greenLo:T.hi,color:ttsEnabled?T.green:T.muted}}>🔊 TTS: {ttsEnabled&&ttsSupport?"ON":"OFF"}</span>
                </div>
              </div>
            </div>
          </Panel>
          <Panel title="QUICK PROMPTS">
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {QUICK_PROMPTS.map(({label,key})=>(
                <button key={key} onClick={()=>sendMsg(key)}
                  style={{background:T.deep,border:`1px solid ${T.border}`,borderRadius:7,padding:"8px 11px",textAlign:"left",cursor:"pointer",fontSize:11,color:T.text,fontFamily:T.font,transition:"background .12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.blueLo}
                  onMouseLeave={e=>e.currentTarget.style.background=T.deep}>
                  {label}
                </button>
              ))}
            </div>
          </Panel>
          <Panel title="COPILOT CAPABILITIES">
            <div style={{display:"flex",flexDirection:"column",gap:7}}>
              {[["📊","Historical Analysis","Past incidents, trends, root cause"],["🔮","Predictive Warnings","7-day risk forecasting per site"],["🛡️","Preventive Actions","Actionable recommendations"],["📋","Report Generation","NL to PDF/Excel report"],["🌿","ESG Insights","Carbon, fuel, sustainability"],["🚦","Traffic Intelligence","Violation patterns, seasonal spikes"]].map(([ic,t,d])=>(
                <div key={t} style={{background:T.deep,borderRadius:7,padding:"8px 10px",border:`1px solid ${T.border}`}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.text}}>{ic} {t}</div>
                  <div style={{fontSize:9,color:T.muted,marginTop:2}}>{d}</div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="PREDICTIVE ALERTS" accent={T.orange}>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {[["⚠️","J-004 GPU Critical","Degradation in ~36h",T.red],["🌧️","Monsoon Risk","Jul 5-12: +38% accidents",T.orange],["💾","Infosys Storage","Quota breach in 12 days",T.accent]].map(([ic,t,d,c])=>(
                <div key={t} style={{background:T.deep,borderRadius:7,padding:"8px 10px",borderLeft:`3px solid ${c}`}}>
                  <div style={{fontSize:11,fontWeight:700,color:c}}>{ic} {t}</div>
                  <div style={{fontSize:9,color:T.muted,marginTop:2}}>{d}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};
